/*
    This module contains all the JWT logic in the App, apart from
    action creators in authSlice.js.

    Provides Login, Refresh and Logout mutations.
*/
// authApiSlice.js
import jwtDecode from 'jwt-decode';
import { apiSlice } from '../../api/apiSlice';
import { logOut, setAccessToken, setUser } from './authSlice';

// Add this new function to check token expiration
const checkTokenExpiration = (token) => {
    if (!token) return true; // Consider expired if no token
    try {
        const decoded = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        return decoded.exp < currentTime;
    } catch (e) {
        return true; // Consider expired if decoding fails
    }
};

// Enhanced onQueryStarted with expiration check
const onQueryStarted = async (arg, { dispatch, queryFulfilled, getState }) => {
    try {
        const { auth } = getState();
        if (auth.accessToken && checkTokenExpiration(auth.accessToken)) {
            dispatch(logOut());
            throw new Error('Sessio on päivääntynyt');
        }

        const response = await queryFulfilled;
        const { accessToken } = response.data;
        dispatch(setAccessToken({ accessToken }));

        const { username, name, role, userId } = jwtDecode(accessToken);
        const user = { username, name, role, userId };
        dispatch(setUser({ user }));
    } catch (e) {
        if (e.message === 'Sessio on päivääntynyt') {
            throw e; // Re-throw to be caught by components
        }
    }
};

// Add a baseQuery interceptor
const baseQueryWithTokenCheck = async (args, api, extraOptions) => {
    const { auth } = api.getState();
    if (auth.accessToken && checkTokenExpiration(auth.accessToken)) {
        api.dispatch(logOut());
        throw new Error('Sessio on päivääntynyt');
    }
    return apiSlice.endpoints[args.endpoint].baseQuery(args, api, extraOptions);
};

export const authApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        login: builder.mutation({
            query: credentials => ({
                url: '/auth/login',
                method: 'POST',
                body: { ...credentials }
            }),
            onQueryStarted
        }),
        refresh: builder.mutation({
            query: () => ({
                url: '/auth/refresh',
                method: 'GET'
            }),
            onQueryStarted
        }),
        sendLogout: builder.mutation({
            query: () => ({
                url: '/auth/logout',
                method: 'POST'
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
                try {
                    await queryFulfilled;
                    dispatch(logOut());

                    // Clear saved username (will be set by InactivityLogout if needed)
                    // This ensures manual logouts don't preserve the username
                    if (!arg?.isAutoLogout) {
                        localStorage.removeItem('savedUsername');
                        localStorage.removeItem('autoLoggedOut');
                        localStorage.removeItem('autoLogoutTime');
                    }

                    setTimeout(() => {
                        dispatch(apiSlice.util.resetApiState());
                    }, 1000);
                } catch (e) {
                    console.log(e);
                }
            }
        }),
    }),
    overrideExisting: false,
});

// Export hooks
export const {
    useLoginMutation,
    useSendLogoutMutation,
    useRefreshMutation
} = authApiSlice;